import { head, isFinite, isFunction, over } from 'lodash';

type FocusOptions = {
	delay?: number;
};

/**
 * Function which triggers `focus` on input element. When used as `ref` handler
 * in React world, this function focuses element prior render, thus performs
 * autofocus.
 */
export function focus(element: HTMLElement | undefined, options?: FocusOptions): void {
	if (options?.delay && options.delay > 5000) {
		throw new Error(
			'Delay for focus cannot be greater than 5 seconds to prevent performance issues'
		);
	}

	function focusElement() {
		element?.focus();
	}

	if (typeof queueMicrotask !== 'undefined') {
		queueMicrotask(() => {
			if (options?.delay) {
				setTimeout(focusElement, options.delay);
			} else {
				focusElement();
			}
		});
	} else if (options?.delay) {
		setTimeout(focusElement, options.delay);
	} else {
		focusElement();
	}
}

/**
 * Search the DOM from the element up its parents and returns first element whose tagName match.
 * @param {HTMLElement} element
 * @param {string} tagName - must be a uppercase
 * @param {number} [maxSearchDepth] - stops the search after `maxSearchDepth` parents, passing 0 tests only the
 * `element`
 * @return {HTMLElement|null}
 */
export const getParentElementByTagName = (
	element: HTMLElement,
	tagName: string,
	maxSearchDepth = -1
): HTMLElement | null => {
	console.assert(tagName.toUpperCase() === tagName, 'Argument `tagName` must be in uppercase');
	let currentElement: HTMLElement | null = element;
	let remainingSearchDepth = maxSearchDepth;
	while (remainingSearchDepth && currentElement) {
		if (currentElement.tagName === tagName) return currentElement;
		currentElement = currentElement.parentNode as HTMLElement | null;
		remainingSearchDepth -= 1;
	}
	return null;
};

/**
 * Detects a HTMLInput containing text (has a box and a `value` property)
 * @param input
 * @return {boolean}
 */
export const isTextualInput = (input: HTMLInputElement): boolean => {
	switch (input.type) {
		case 'date':
		case 'datetime-local':
		case 'email':
		case 'month':
		case 'number':
		case 'password':
		case 'range':
		case 'search':
		case 'tel':
		case 'text':
		case 'time':
		case 'url':
		case 'week':
			return true;
		default:
			return false;
	}
};

/**
 * Allows register multiple handlers for single event. Handlers are filtered for
 * function, so event empty handlers could be provided. Useful when you need to
 * run some handler and also allow other users to pass handlers via `props`.
 * @param handlers
 * @return { any | any[] } NOTE: React event system currently not use return
 * values from handlers, therefor this function not care for return values and
 * might return anything. If React API changes, this function must be refactored.
 * @see https://reactjs.org/docs/handling-events.html
 */
export function overHandlers(...handlers) {
	const onlyFunctions = handlers.filter(isFunction);
	// eslint-disable-next-line no-nested-ternary
	return onlyFunctions.length === 0
		? null
		: onlyFunctions.length === 1
			? head(onlyFunctions)
			: over(onlyFunctions);
}

/**
 * Handler that simply sets preventDefault on event. Use it instead ad-hoc
 * anonymous functions.
 * @param event
 * @return {*}
 */
export function preventDefault(event: Event): void {
	event.preventDefault();
}

export function stopPropagation(event: Event): void {
	event.preventDefault();
	event.stopPropagation();
}

/**
 * Dispatches a click even on domNode
 * @param {Node} domNode
 */
export function clickOn(domNode: Node): void {
	domNode.dispatchEvent(
		new MouseEvent('click', {
			bubbles: true,
			cancelable: true,
			view: window,
		})
	);
}

/**
 * This is a helper for setting a onClick handler which executes sooner than regular onClick. This is needed for some
 * edge-cases. Handles both MouseEvent and TouchEvent.
 * @param {function(event: MouseEvent | TouchEvent): void} handler
 * @return {{onTouchEnd, onMouseDown}}
 */
export const onBeforeClick = (
	handler: (event: MouseEvent | TouchEvent) => void
): { onTouchEnd: (event: TouchEvent) => void; onMouseDown: (event: MouseEvent) => void } => ({
	onMouseDown: handler,
	onTouchEnd: handler,
});

export function scrollCurrentBy(y: number): void {
	const mobAppElement = document.getElementById('MobileAppLayoutContent');

	if (process.env.MOB_APP_BUILD && !mobAppElement) {
		return;
	}

	const top = process.env.MOB_APP_BUILD
		? mobAppElement?.scrollTop
		: document.documentElement.scrollTop || document.body.scrollTop;

	const scrollElement = process.env.MOB_APP_BUILD ? mobAppElement : window;

	if (!isFinite(y) || !isFinite(top) || !scrollElement) {
		return;
	}

	scrollElement.scrollTo({
		top: Math.abs(y + (top || 0)),
	});
}

/**
 * Scrolls to top of the document
 */
export function scrollToTop(): void {
	const c = process.env.MOB_APP_BUILD
		? document.getElementById('MobileAppLayoutContent')?.scrollTop
		: document.documentElement.scrollTop || document.body.scrollTop;

	if (c && c > 0) {
		const scrollElement = process.env.MOB_APP_BUILD
			? document.getElementById('MobileAppLayoutContent')
			: window;

		window.requestAnimationFrame(scrollToTop);
		scrollElement?.scrollTo(0, c - Math.max(10, c / 10));
	}
}

/**
 * Creates DOMNode input[type="file"] for opening the "Choose file" dialog programmatically by dispatching the click
 * event.
 * !!! For security reasons, the click event must be dispatched in reaction to the user action, dispatching the event in
 * another event handler triggered by the user action will work, dispatching the event delayed or randomly without user
 * action will not work.
 * @param {object} options
 * @property {string} [accept] - unique file type specifier.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#unique_file_type_specifiers
 * @property {"user" | "environment"} [capture] - only for image or voice upload, specifies, which camera/microphone the
 * user-agent shall activate.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#capture
 * @property {boolean} [multiple] - to allow upload more than one file.
 * @property {function(files: FileList): void} onChange - will be called with files picked by the user.
 * @return {HTMLInputElement}
 */
export const createFileInput = ({ accept, capture, multiple, onChange }) => {
	const input = document.createElement('input');
	input.accept = accept;

	if (capture) {
		input.capture = capture;
	}

	input.multiple = multiple;
	input.onchange = (event) => onChange(event.target?.files);
	input.type = 'file';
	return input;
};

export const LOW_PRIORITY = 'low';
export const HIGH_PRIORITY = 'high';

/**
 * Appends a `<script>` tag to the page with a Promise API
 * @param {string} src the URL to the script
 * @param {boolean} [async=true] load the script asynchronously
 * @param {string} [id] value for the `id` attribute on the Script tag
 * @param {LOW_PRIORITY | HIGH_PRIORITY} priority loading priority
 * @returns {Promise<unknown>}
 */
export const loadExternalScript = ({ src, async = true, id, priority }) =>
	new Promise((resolve, reject) => {
		const e = document.querySelector('script');
		const s = document.createElement('script');
		s.setAttribute('type', 'text/javascript');
		if (async) s.setAttribute('async', 'true');
		s.setAttribute('src', src);
		if (id) {
			s.id = id;
		}
		if (priority && [LOW_PRIORITY, HIGH_PRIORITY].includes(priority)) {
			s.setAttribute('fetchpriority', priority);
		}
		e?.parentNode?.appendChild(s);
		s.onload = resolve;
		s.onerror = reject;
	});
